import { Icon, Logo } from '@eleo-rh/core-ui'
import { Divider, Popover } from 'antd'
import CoreUIButton from 'components/UI/CoreUIButton'
import Spin from 'components/UI/Spin'
import Tooltip from 'components/UI/Tooltip'
import { API_APPLICATIONS } from 'constants/api-v2'
import { MODULE_CLASSNAMES, MODULE_DEMO_LINK } from 'containers/Admin/Settings/ModulesAndApps'
import { useI18n } from 'context/I18nProvider'
import { useProfile } from 'context/ProfileProvider'
import { useModules } from 'hooks/modules'
import useSWR from 'hooks/swr'
import { useNavigate, useRouteLoaderData } from 'react-router-dom'
import type { Application, Settings } from 'shared/types'

const LOGO_SIZE = 32

const AppsMenu = () => {
  const settings = useRouteLoaderData('root') as Settings
  const { profile, hasPermission } = useProfile()
  const { i18n, locale } = useI18n()
  const navigate = useNavigate()

  const isSuperAdmin = profile.type === 'super-admin'
  const canAskForDemo = profile.type !== 'collaborator' && profile.type !== 'manager'

  const { modules, isLoading: areModulesLoading } = useModules()
  const { data: externalApps, isValidating: areExternalAppsLoading } = useSWR<Application[]>(API_APPLICATIONS)
  const externalAppsList = Array.isArray(externalApps) ? externalApps.filter(app => app.enabled) : []

  return (
    <Popover
      trigger={'click'}
      arrow={false}
      placement="bottomRight"
      content={
        areModulesLoading || areExternalAppsLoading ? (
          <Spin size="small" />
        ) : (
          <div className={[externalAppsList.length > 0 ? 'grid-cols-4' : 'grid-cols-2', 'grid gap-3'].join(' ')}>
            {externalAppsList.length > 0 && (
              <span className="col-span-4 capitalize font-bold">{i18n('modules.title.eleo')}</span>
            )}
            {modules?.map(module => {
              const isActive = module.enabled
              const isIncluded = module.included
              const isClickable = isSuperAdmin && isIncluded && !isActive
              const activeClassName = MODULE_CLASSNAMES[module.slug]
                ? `${MODULE_CLASSNAMES[module.slug].bg} border ${MODULE_CLASSNAMES[module.slug].border}`
                : 'bg-slate-50 border border-slate-300'

              return (
                <Tooltip
                  key={module.name}
                  color="var(--color-slate-50)"
                  title={
                    !isSuperAdmin && isIncluded && !isActive
                      ? i18n('modules.tooltip.contact')
                      : !isIncluded &&
                        canAskForDemo && (
                          <CoreUIButton
                            size="small"
                            variant="secondary"
                            onClick={() => window.open(MODULE_DEMO_LINK, '_blank', 'noopener,noreferrer')}
                          >
                            {i18n('modules.label.ask_for_demo')}
                          </CoreUIButton>
                        )
                  }
                >
                  <button
                    type="button"
                    disabled={!isIncluded}
                    onClick={
                      module.slug === 'onboarding' && isActive && isIncluded
                        ? () => window.open(settings.onboardingURL, '_blank', 'noopener,noreferrer')
                        : isClickable
                          ? () => navigate(`/${locale}/admin/modules&applications?highlightModule=${module.slug}`)
                          : undefined
                    }
                    className={[
                      'flex flex-col items-center gap-2 px-2 py-3 rounded-xxs max-w-24',
                      !isIncluded ? 'text-slate-300 cursor-not-allowed' : '',
                      isActive ? activeClassName : 'bg-white',
                      isClickable ? 'cursor-pointer hover:bg-slate-100' : '',
                    ].join(' ')}
                  >
                    <Logo
                      size={LOGO_SIZE}
                      module={module.slug}
                      showModuleName={false}
                      logoColor={!isIncluded ? 'var(--color-slate-300)' : undefined}
                    />
                    <span>{module.name}</span>
                  </button>
                </Tooltip>
              )
            })}
            {externalAppsList.length > 0 && (
              <>
                <Divider styles={{ root: { margin: 4 } }} className="col-span-4" />
                <span className="col-span-4 capitalize font-bold">{i18n('modules.title.external_tools')}</span>
                {externalAppsList.map(app => (
                  <button
                    key={app.name}
                    type="button"
                    className={[
                      'max-w-24 flex flex-col items-center gap-2 px-2 py-3 rounded-xxs',
                      app.enabled ? 'bg-slate-50 border border-slate-300' : '',
                      app.enabled || hasPermission('ADMIN_SETTINGS')
                        ? 'hover:bg-slate-100 cursor-pointer'
                        : ' cursor-not-allowed',
                    ].join(' ')}
                    onClick={
                      app.enabled
                        ? () => window.open(app.url, '_blank', 'noopener,noreferrer')
                        : hasPermission('ADMIN_SETTINGS')
                          ? () => navigate(`/${locale}/admin/modules&applications?highlightApplication=${app.id}`)
                          : undefined
                    }
                  >
                    <img
                      src={app.icon?.url}
                      alt={app.name}
                      className="w-full object-cover rounded-xxs"
                      style={{ height: LOGO_SIZE }}
                    />
                    <span title={app.name} className="w-full truncate text-center">
                      {app.name}
                    </span>
                  </button>
                ))}
              </>
            )}
          </div>
        )
      }
    >
      <CoreUIButton variant="tertiary" status="neutral" shape="circle">
        <Icon name="grid-2" />
      </CoreUIButton>
    </Popover>
  )
}

export default AppsMenu
